import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { cards } from '../assets/cards';

import { HTV2ExploreMarketplace } from './components/HTV2ActionCardLinkCTA';
import { HTV2CreatePageCTA, HTV2CreateWhiteboardCTA } from './components/HTV2CreateContentCTA';
import { HTV2CreateSpaceCTA } from './components/HTV2CreateSpaceCTA';
import { HTV2ExploreAICTA } from './components/HTV2ModalDialogCTA';
import {
	commonCardI18n,
	RenderedFormattedMessageCreateCTA,
	RenderedFormattedMessageCreateTitle,
} from './HTV2CommonCardsI18n';
import type { OnboardingActionStates } from './HTV2CardTypes';

const i18n = defineMessages({
	exploreAITitle: {
		id: 'experiment-home-onboarding-takeover-v2.explore-ai.title',
		defaultMessage: 'Explore Confluence AI',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	exploreAIDescription: {
		id: 'experiment-home-onboarding-takeover-v2.explore-ai.description',
		defaultMessage: 'Search, write, and act faster - available on your automatic Premium trial.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	exploreAICTA: {
		id: 'experiment-home-onboarding-takeover-v2.explore-ai.cta',
		defaultMessage: 'Watch video',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will show an embedded youtube video',
	},
	thirdPartyAppsTitle: {
		id: 'experiment-home-onboarding-takeover-v2.third-party-apps.title',
		defaultMessage: 'Connect 3rd party apps',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	thirdPartyAppsDescription: {
		id: 'experiment-home-onboarding-takeover-v2.third-party-apps.description',
		defaultMessage: 'Customize Confluence with apps that fit your team’s workflow.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	thirdPartyAppsCTA: {
		id: 'experiment-home-onboarding-takeover-v2.third-party-apps.cta',
		defaultMessage: 'Explore apps',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will create a page and land them in the editor',
	},
});

export const XFLOW_ONBOARDING_ACTIONS_DATA = [
	{
		id: 'createPage',
		title: <RenderedFormattedMessageCreateTitle />,
		description: commonCardI18n.createPageDescription,
		image: cards.createPage,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2CreatePageCTA onClick={onClick}>
				<RenderedFormattedMessageCreateCTA />
			</HTV2CreatePageCTA>
		),
	},
	{
		id: 'createSpace',
		title: <FormattedMessage {...commonCardI18n.createSpaceTitle} />,
		description: commonCardI18n.createSpaceDescription,
		image: cards.createSpace,
		cta: ({ onClick }: { onClick: () => void }) => <HTV2CreateSpaceCTA onClick={onClick} />,
	},
	{
		id: 'createWhiteboard',
		title: <FormattedMessage {...commonCardI18n.createWhiteboardTitle} />,
		description: commonCardI18n.createWhiteboardDescription,
		image: cards.createWhiteboard,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2CreateWhiteboardCTA onClick={onClick}>
				<FormattedMessage {...commonCardI18n.createWhiteboardCTA} />
			</HTV2CreateWhiteboardCTA>
		),
	},
	{
		id: 'exploreAI',
		title: <FormattedMessage {...i18n.exploreAITitle} />,
		description: i18n.exploreAIDescription,
		image: cards.exploreAI,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2ExploreAICTA onClick={onClick}>
				<FormattedMessage {...i18n.exploreAICTA} />
			</HTV2ExploreAICTA>
		),
		keepCtaOnCompleted: true,
	},
	{
		id: 'exploreMarketplace',
		title: <FormattedMessage {...i18n.thirdPartyAppsTitle} />,
		description: i18n.thirdPartyAppsDescription,
		image: cards.exploreMarketplace,
		cta: ({ onClick }: { onClick: () => void }) => (
			<HTV2ExploreMarketplace onClick={onClick}>
				<FormattedMessage {...i18n.thirdPartyAppsCTA} />
			</HTV2ExploreMarketplace>
		),
	},
] as const;

export const transformXflowOnboardingActions = ({
	onboardingActionStates,
}: {
	onboardingActionStates: OnboardingActionStates;
}) => {
	return XFLOW_ONBOARDING_ACTIONS_DATA.map((action) => ({
		...action,
		state: onboardingActionStates[action.id] || 'pending',
	}));
};
